import {
  deadlineStatus,
  formatDate,
  formatMoney,
  formatMonth,
  monthOf,
  roundCents,
  type ZeroReport,
} from "@pcj/shared";
import { CalendarClock, RotateCcw } from "lucide-react";
import { useState } from "react";
import { type MonthFolder, MonthFolders } from "../../components/MonthFolders";
import { Banner, Button, EmptyState, Pill, SectionTitle, Table, td, th } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { capitalizeFirst, DEADLINE_TONES, deadlineLabel, plural } from "../../lib/format";
import { useCompany } from "./CompanyLayout";
import { FilingDialog } from "./FilingDialog";

/**
 * Notices (Avisos) in one folder per month, each folder opening that month's
 * notices; and zero reports (Informes en cero). Both are due on the 17th of
 * the following month.
 */
export function ReportsTab() {
  const { notices, zeroReports, today, can, actions } = useCompany();
  const [filing, setFiling] = useState<ZeroReport | null>(null);
  const manage = can("manageCompliance");
  const mutationError = actions.fileZeroReport.error ?? actions.unfileZeroReport.error;

  const folders: MonthFolder[] = [...new Set(notices.map((n) => n.month))].map((month) => {
    const monthNotices = notices.filter((n) => n.month === month);
    const pending = monthNotices.filter((n) => !n.filed);
    const amount = monthNotices.reduce((sum, n) => roundCents(sum + n.amount), 0);
    const deadline = monthNotices[0]?.deadline ?? "";
    const status = pending.length > 0 ? deadlineStatus(deadline, today) : "filed";
    return {
      month,
      title: plural(monthNotices.length, "Aviso", "Avisos"),
      detail: `${formatMoney(amount)} sin IVA`,
      badges: (
        <Pill tone={DEADLINE_TONES[status]}>
          {pending.length === 0
            ? "Presentados"
            : `${plural(pending.length, "pendiente", "pendientes")} · ${deadlineLabel(status, deadline, today)}`}
        </Pill>
      ),
    };
  });

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <SectionTitle
          title="Avisos"
          subtitle="Una carpeta por mes con Avisos: operaciones que alcanzan 1,605 UMA, individuales o acumuladas en 6 meses"
        />
        {notices.length === 0 ? (
          <EmptyState icon={CalendarClock} title="Ninguna operación ha requerido Aviso" />
        ) : (
          <MonthFolders
            folders={folders}
            currentMonth={monthOf(today)}
            yearSummary={(_, yearFolders) => plural(yearFolders.length, "mes con Avisos", "meses con Avisos")}
          />
        )}
      </section>

      <section className="space-y-3">
        <SectionTitle
          title="Informes en cero"
          subtitle="Meses cerrados sin ningún Aviso: se presenta un informe en cero con el mismo plazo"
        />
        {mutationError && !filing && <Banner tone="error">{errorMessage(mutationError)}</Banner>}
        {zeroReports.length === 0 ? (
          <EmptyState icon={CalendarClock} title="Aún no hay meses cerrados">
            El primer informe se generará al terminar el mes en curso.
          </EmptyState>
        ) : (
          <Table>
            <thead className="bg-gray-50">
              <tr>
                <th className={th}>Mes</th>
                <th className={th}>Fecha límite</th>
                <th className={th}>Estatus</th>
                <th className={th} aria-label="Acciones" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {zeroReports.map((report) => (
                <tr key={report.month}>
                  <td className={`${td} text-gray-700`}>{capitalizeFirst(formatMonth(report.month))}</td>
                  <td className={`${td} text-gray-600`}>{report.required ? formatDate(report.deadline) : "—"}</td>
                  <td className={td}>
                    <Pill tone={DEADLINE_TONES[report.status]}>
                      {report.required
                        ? deadlineLabel(report.status, report.deadline, today)
                        : "No requerido (hubo Aviso)"}
                    </Pill>
                    {report.record?.folio && <p className="mt-1 text-xs text-gray-500">Acuse {report.record.folio}</p>}
                  </td>
                  <td className={`${td} text-right`}>
                    {manage && report.required && !report.record && (
                      <Button size="sm" onClick={() => setFiling(report)}>
                        Marcar presentado
                      </Button>
                    )}
                    {manage && report.record && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={RotateCcw}
                        onClick={() => actions.unfileZeroReport.mutate(report.month)}
                      >
                        Deshacer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <FilingDialog
        open={filing !== null}
        title={filing ? `Informe en cero de ${formatMonth(filing.month)}` : ""}
        description="Registra que presentaste el informe en cero de este mes en el portal del SAT."
        pending={actions.fileZeroReport.isPending}
        error={filing ? mutationError : null}
        onClose={() => setFiling(null)}
        onConfirm={(folio) =>
          filing &&
          actions.fileZeroReport.mutate(
            { month: filing.month, folio: folio || undefined },
            { onSuccess: () => setFiling(null) },
          )
        }
      />
    </div>
  );
}
